// Y-axis domains for the SVG charts. Kept out of format.ts: that module turns
// numbers into strings, this one decides which numbers the axis spans.

/**
 * The y-domain for a set of values, zero-anchored so bars and areas measure
 * from a meaningful baseline: all-positive data gives `[0, max]` (what the
 * charts did before this existed), all-negative gives `[min, 0]`, and mixed
 * data spans `[min, max]` so negative points stay inside the plot.
 *
 * `ymin=` / `ymax=` on the fence override either end — `ymin=0` pins a bar
 * chart to zero, `ymax=100` holds a percentage axis steady across pages. A
 * non-numeric or empty value is ignored rather than collapsing the axis.
 */
export function yDomain(
  vals: number[],
  attrs: Record<string, string>,
): [number, number] {
  const finite = vals.filter((v) => Number.isFinite(v));
  let lo = Math.min(0, ...finite);
  let hi = Math.max(0, ...finite);

  const pinned = (key: string) => {
    const n = Number(attrs[key]);
    return attrs[key]?.trim() && Number.isFinite(n) ? n : undefined;
  };
  lo = pinned("ymin") ?? lo;
  hi = pinned("ymax") ?? hi;

  // A zero-height domain divides by zero downstream; give it 1 unit of span.
  if (!(hi > lo)) return [lo, lo + 1];
  return [lo, hi];
}

/** Where the zero line sits, clamped into a domain that may not contain it. */
export function baselineOf([lo, hi]: [number, number]): number {
  return Math.min(Math.max(0, lo), hi);
}
